/**
 * License Inventory Component
 * Displays list of licenses with filtering, sorting, and row selection using design system ListRow
 * Uses Typography component for consistent text styling across the component
 */

import React, { useState } from 'react';
import {
  ApplicationIcon,
  Icon,
  ListRow,
  Typography,
} from '@saviynt/design-system';

import SliderComponent from '../../../../../shared/components/SliderComponent';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import LICENSE_INVENTORY_MOCK_DATA from '../../../mock-data/licenseInventoryMockData';

import {
  LICENSE_INVENTORY_SYMBOLS,
  LICENSE_RISKS,
  TREND_DIRECTIONS,
} from './constants';
import type { License, LicenseInventoryProps } from './type';

import './LicenseInventory.css';

const mockLicenses: License[] = LICENSE_INVENTORY_MOCK_DATA;

/**
 * License Inventory component. Displays a list of licenses with filtering, sorting, and row selection capabilities.
 * Uses the design system ListRow component for consistent styling and behavior.
 */
function LicenseInventory({
  onSelectLicense,
}: LicenseInventoryProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(
    null
  );

  const handleRowClick = (licenseId: string): void => {
    setSelectedLicenseId(licenseId);
    onSelectLicense?.(licenseId);
  };

  const getRiskBadgeClass = (risk: string): string => {
    switch (risk) {
      case LICENSE_RISKS.LOW:
        return 'licenseRiskBadge__low';

      case LICENSE_RISKS.MEDIUM:
        return 'licenseRiskBadge__medium';

      case LICENSE_RISKS.HIGH:
        return 'licenseRiskBadge__high';

      default:
        return 'licenseRiskBadge__low';
    }
  };

  const getTrendDirection = (trend: number): string =>
    trend > 0 ? TREND_DIRECTIONS.UP : TREND_DIRECTIONS.DOWN;

  const renderCenterContent = (license: License): React.ReactElement => (
    <div className='rowContent'>
      {/* Labels Row */}
      <div className='labelsRow'>
        <Typography kind='body3' className='labelCell'>
          {localize('Vendor')}
        </Typography>
        <Typography kind='body3' className='labelCell'>
          {localize('Status')}
        </Typography>
        <Typography kind='body3' className='labelCell'>
          {localize('Utilization')}
        </Typography>
        <Typography kind='body3' className='labelCell'>
          {localize('Annual Cost')}
        </Typography>
        <Typography kind='body3' className='labelCell'>
          {localize('Renewal Date')}
        </Typography>
        <Typography kind='body3' className='labelCell'>
          {localize('Risk')}
        </Typography>
      </div>

      {/* Content Row */}
      <div className='contentRow'>
        {/* Vendor */}
        <div className='cell licenseCell__vendor'>
          <Typography kind='body2-bold' className='licenseCell__vendorName'>
            {license.vendor}
          </Typography>
          <Typography kind='body3' className='licenseCell__category'>
            {license.category}
          </Typography>
        </div>

        {/* Status */}
        <div className='cell licenseCell__status'>
          <div className='licenseStatusBadge'>
            <Typography kind='body3'>{license.status}</Typography>
          </div>
        </div>

        {/* Utilization */}
        <div className='cell licenseCell__utilization'>
          <div className='licenseUtilization__values'>
            <Typography kind='body2-bold' className='licenseUtilization__count'>
              {license.utilization.toLocaleString()}
              {LICENSE_INVENTORY_SYMBOLS.SLASH}
              {license.utilizationMax.toLocaleString()}
            </Typography>
            <Typography kind='body3' className='licenseUtilization__percent'>
              {license.utilizationPercent}
              {LICENSE_INVENTORY_SYMBOLS.PERCENT}
            </Typography>
          </div>
          <div className='licenseUtilization__sliderWrapper'>
            <SliderComponent
              value={license.utilizationPercent}
              readOnly
              progressColor='var(--color-blue-500, #0294f7)'
              trackHeight='0.25rem'
            />
          </div>
        </div>

        {/* Annual Cost */}
        <div className='cell licenseCell__cost'>
          <Typography kind='body2-bold' className='licenseCost__amount'>
            {LICENSE_INVENTORY_SYMBOLS.DOLLAR}
            {license.annualCost.toLocaleString()}
          </Typography>
          <div
            className={`licenseCost__trend licenseCost__trend--${getTrendDirection(
              license.costTrend
            )}`}>
            <Typography kind='body3' className='licenseCost__trendText'>
              {LICENSE_INVENTORY_SYMBOLS.PLUS}
              {license.costTrend}
              {LICENSE_INVENTORY_SYMBOLS.PERCENT_YOY}
            </Typography>
            <Icon
              kind={
                getTrendDirection(license.costTrend) === TREND_DIRECTIONS.UP
                  ? 'TrendUp'
                  : 'TrendDown'
              }
              size='small'
              className='licenseCost__trendIcon'
            />
          </div>
        </div>

        {/* Renewal Date */}
        <div className='cell licenseCell__renewal'>
          <Typography kind='body2' className='licenseRenewal__date'>
            {license.renewalDate}
          </Typography>
        </div>

        {/* Risk */}
        <div className='cell licenseCell__risk'>
          <div
            className={`licenseRiskBadge ${getRiskBadgeClass(license.risk)}`}>
            <Typography kind='body3'>{license.risk}</Typography>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className='root'>
      <div className='body'>
        {mockLicenses.map((license) => (
          <ListRow
            key={license.id}
            leadingComp={<ApplicationIcon logo={license.logo} size='medium' />}
            centerContent={renderCenterContent(license)}
            handleRowClick={() => handleRowClick(license.id)}
            isCheckboxDisabled
            className={selectedLicenseId === license.id ? 'rowSelected' : ''}
            dataTestId={`license-row-${license.id}`}
          />
        ))}
      </div>
    </div>
  );
}

export default LicenseInventory;
